'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var react = require('react');

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

var isVisibleEvents = ['scroll', 'resize', 'orientationchange', 'mousemove', 'mouseover', 'mouseout', 'mouseenter', 'mouseleave', 'mousedown', 'mouseup', 'mousewheel', 'focus', 'focusin', 'focusout', 'compositionstart', 'compositionupdate', 'animationstart', 'animationend', 'pointermove', 'click', 'touchstart', 'touchend', 'touchcancel', 'touchmove', 'gesturestart', 'gestureend', 'gesturechange', 'transitionstart', 'transitionend'];
var callBackWithRetries = function (callback) {
  return function (event) {
    callback(event);
    var interval = setInterval(function () {
      return callback(event);
    }, 300);
    setTimeout(function () {
      clearInterval(interval);
    }, 6000);
  };
};
var tryRegisteringWindowEvent = function (callback) {
  return function (event) {
    try {
      window.addEventListener(event, callBackWithRetries(callback), {
        passive: true
      });
    } catch (e) {
      console.error(e);
    }
  };
};
var tryDeregisteringWindowEvent = function (callback) {
  return function (event) {
    try {
      window.removeEventListener(event, callback);
    } catch (e) {
      console.error(e);
    }
  };
};
var useIsVisible = function (previewElement) {
  var _a = __read(react.useState(false), 2),
    isVisible = _a[0],
    setIsVisible = _a[1];
  var isElementVisible = react.useCallback(function (_a) {
    var _b = _a.window,
      innerHeight = _b.innerHeight,
      innerWidth = _b.innerWidth,
      _c = _a.document.documentElement,
      clientHeight = _c.clientHeight,
      clientWidth = _c.clientWidth;
    if (!previewElement.current) {
      return false;
    }
    var _d = previewElement.current.getBoundingClientRect(),
      top = _d.top,
      left = _d.left,
      bottom = _d.bottom,
      right = _d.right;
    var height = innerHeight || clientHeight;
    var width = innerWidth || clientWidth;
    var verticallyInside = function (spot) {
      return spot >= 0 && spot <= height;
    };
    var horizontallyInside = function (spot) {
      return spot >= 0 && spot <= width;
    };
    var topInside = verticallyInside(top);
    var bottomInside = verticallyInside(bottom);
    var leftInside = horizontallyInside(left);
    var rightInside = horizontallyInside(right);
    return topInside && leftInside || topInside && rightInside || bottomInside && leftInside || bottomInside && rightInside;
  }, [previewElement]);
  var startLoadIfVisible = react.useCallback(function (_a) {
    var window = _a.window,
      document = _a.document;
    if (!isVisible && isElementVisible({
      window: window,
      document: document
    })) {
      setIsVisible(true);
    }
  }, [isElementVisible, isVisible]);
  react.useEffect(function () {
    var loadBasedOnPosition = function () {
      return startLoadIfVisible({
        window: window,
        document: document
      });
    };
    loadBasedOnPosition();
    if (!isVisible) {
      isVisibleEvents.forEach(tryRegisteringWindowEvent(loadBasedOnPosition));
    }
    return function () {
      isVisibleEvents.forEach(tryDeregisteringWindowEvent(loadBasedOnPosition));
    };
  }, [startLoadIfVisible, isVisible]);
  return isVisible;
};

function useScrollWatcher(_a) {
  var _b = _a.id,
    id = _b === void 0 ? '' : _b;
  var _c = __read(react.useState(0), 2),
    scrollTop = _c[0],
    setScrollTop = _c[1];
  var _d = __read(react.useState(1), 2),
    verticalHeight = _d[0],
    setVerticalHeight = _d[1];
  var _e = __read(react.useState(false), 2),
    isScrollingUp = _e[0],
    setIsScrollingUp = _e[1];
  react.useEffect(function () {
    var onScroll = function (e) {
      if (typeof document !== 'undefined' && typeof window !== 'undefined') {
        var currentScrollTop = window.scrollY;
        var currentVerticalHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
        setScrollTop(currentScrollTop);
        setVerticalHeight(currentVerticalHeight);
        if (currentVerticalHeight === verticalHeight) {
          var currentIsScrollingUp = currentScrollTop <= 0 || currentScrollTop < scrollTop;
          setIsScrollingUp(currentIsScrollingUp);
        }
      }
    };
    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      window.addEventListener("scroll", onScroll);
      return function () {
        return window.removeEventListener("scroll", onScroll);
      };
    }
  }, [id, scrollTop, verticalHeight]);
  var isAtTheTop = react.useMemo(function () {
    if (typeof window === 'undefined') {
      return true;
    }
    var scrollPercent = scrollTop / verticalHeight * 100;
    return scrollPercent <= 0;
  }, [scrollTop, verticalHeight]);
  return {
    isAtTheTop: isAtTheTop,
    isScrollingUp: isScrollingUp
  };
}

var browsersNamePattern = /(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i;
var getBrowserNameAndVersion = function () {
  try {
    if (typeof window !== 'undefined' && typeof window.navigator === 'object') {
      var browserMatches = window.navigator.userAgent.match(browsersNamePattern) || [];
      if (/trident/i.test(browserMatches[1])) {
        var tem = /\brv[ :]+(\d+)/g.exec(window.navigator.userAgent) || [];
        return {
          name: 'IE',
          version: tem[1] || 0
        };
      }
      if (browserMatches[1] === 'Chrome') {
        var operaVersionMatch = window.navigator.userAgent.match(/\bOPR|Edge\/(\d+)/);
        if (operaVersionMatch !== null) {
          return {
            name: 'Opera',
            version: operaVersionMatch[1]
          };
        }
      }
      var nameAndVersionMatch = browserMatches[2] ? [browserMatches[1], browserMatches[2]] : [navigator.appName, navigator.appVersion, '-?'];
      var versionRegexMatch = window.navigator.userAgent.match(/version\/(\d+)/i);
      if (versionRegexMatch !== null) {
        nameAndVersionMatch.splice(1, 1, versionRegexMatch[1]);
      }
      var browsername = nameAndVersionMatch[0];
      var version = parseFloat(nameAndVersionMatch[1]);
      if (!Number.isNaN(version)) {
        return {
          name: browsername,
          version: version
        };
      }
    }
  } catch (error) {
    // Failed to get browser version
  }
  return null;
};

var shouldRender = function () {
  var browser = getBrowserNameAndVersion();
  var shouldSendTrue = !browser || /safari/ig.test(browser.name) && typeof browser.version === 'number' && browser.version < 12;
  if (typeof IntersectionObserver === 'undefined') {
    return true;
  }
  return shouldSendTrue;
};
function useOnScreen(ref) {
  var _a = __read(react.useState(shouldRender()), 2),
    isIntersecting = _a[0],
    setIntersecting = _a[1];
  react.useEffect(function () {
    if (!isIntersecting && ref.current) {
      try {
        var observer_1 = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            return setIntersecting(function (visible) {
              return visible || entry.isIntersecting;
            });
          });
        });
        observer_1.observe(ref.current);
        // Remove the observer as soon as the component is unmounted
        return function () {
          observer_1.disconnect();
        };
      } catch (err) {
        console.log("useOnScreen failed: ".concat(err));
        setIntersecting(true);
      }
    }
  }, [ref, isIntersecting]);
  return isIntersecting;
}

function useIsAtTheTop(_a) {
  var _b = _a.id,
    id = _b === void 0 ? '' : _b;
  var _c = __read(react.useState(0), 2),
    scrollTop = _c[0],
    setScrollTop = _c[1];
  var _d = __read(react.useState(1), 2),
    verticalHeight = _d[0],
    setVerticalHeight = _d[1];
  react.useEffect(function () {
    var onScroll = function (e) {
      if (typeof document !== 'undefined' && typeof window !== 'undefined') {
        var currentScrollTop = window.scrollY;
        var currentVerticalHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
        setScrollTop(currentScrollTop);
        setVerticalHeight(currentVerticalHeight);
      }
    };
    if (typeof document !== 'undefined' && typeof window !== 'undefined') {
      window.addEventListener("scroll", onScroll);
      return function () {
        return window.removeEventListener("scroll", onScroll);
      };
    }
  }, [id, scrollTop, verticalHeight]);
  var isAtTheTop = react.useMemo(function () {
    var scrollPercent = scrollTop / verticalHeight * 100;
    return scrollPercent <= 0;
  }, [scrollTop, verticalHeight]);
  return isAtTheTop || typeof window === 'undefined';
}

exports.useIsAtTheTop = useIsAtTheTop;
exports.useIsVisible = useIsVisible;
exports.useOnScreen = useOnScreen;
exports.useScrollWatcher = useScrollWatcher;
//# sourceMappingURL=hooks.js.map
